import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "@/components/Modals/Modal";
import {
  fetchInformesFiscales,
  generarInformeFiscal,
} from "@/features/evento/eventoThunks";

function formatFechaInforme(fechaStr) {
  if (!fechaStr) return "N/A";
  const d = new Date(fechaStr);
  if (isNaN(d.getTime())) return "N/A";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
}

function formatMontoCLP(monto) {
  if (monto === null || monto === undefined) return "$0";
  return "$" + Number(monto).toLocaleString("es-CL");
}

export default function ModalInformesFiscales({ isOpen, onClose, uidEvento }) {
  const dispatch = useDispatch();
  const { informesFiscales, generandoInforme, errorInforme } = useSelector(
    (state) => state.evento
  );

  useEffect(() => {
    if (isOpen && uidEvento) {
      dispatch(fetchInformesFiscales({ uidEvento }));
    }
  }, [isOpen, uidEvento, dispatch]);

  const handleGenerarInforme = async () => {
    const result = await dispatch(generarInformeFiscal({ uidEvento }));
    if (result?.payload?.informe?.urlDescarga) {
      window.open(result.payload.informe.urlDescarga, "_blank");
    }
  };

  const handleDescargar = (urlDescarga) => {
    if (urlDescarga) {
      window.open(urlDescarga, "_blank");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} large="max-w-3xl">
      <div className="bg-aw-bluedark inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green rounded-xl border border-gray-600 p-6">
        {/* Título */}
        <div className="mb-5">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <FontAwesomeIcon
              icon={["fas", "file-excel"]}
              className="text-aw-green"
            />
            Informes Fiscales
          </h2>
        </div>

        {/* Texto explicativo */}
        <p className="text-sm text-gray-400 mb-6 leading-5">
          Genera informes Excel con los datos fiscales de las ventas de tu evento
          para cumplir con la emisión de boletas electrónicas o facturas. Cada
          informe incluye solo los carritos que aún no han sido reportados, sean 
          de compra directa de tickets o de invitaciones en sus diferentes modalidades.
        </p>

        {/* Mensaje de error */}
        {errorInforme && (
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-900/40 border border-red-500/50 px-4 py-3">
            <FontAwesomeIcon
              icon={["fas", "circle-exclamation"]}
              className="text-red-400 mt-0.5 shrink-0"
            />
            <p className="text-sm text-red-300">{errorInforme}</p>
          </div>
        )}

        {/* Lista de informes */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide mb-3">
            Informes generados
          </h3>

          {informesFiscales && informesFiscales.length > 0 ? (
            <div className="space-y-2">
              {informesFiscales.map((informe, index) => (
                <div
                  key={informe.uid || index}
                  className="flex items-center justify-between gap-4 rounded-lg bg-aw-bluedark-50 border border-gray-700 px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FontAwesomeIcon
                      icon={["fas", "file-excel"]}
                      className="text-green-400 shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {informe.nombreArchivo || `Informe #${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatFechaInforme(informe.createdAt)} &middot;{" "}
                        {informe.cantidadCarritos ?? 0} carritos &middot;{" "}
                        {formatMontoCLP(informe.montoTotal)}
                      </p>
                    </div>
                  </div>

                  {informe.urlDescarga ? (
                    <button
                      onClick={() => handleDescargar(informe.urlDescarga)}
                      className="shrink-0 flex items-center gap-2 text-xs font-semibold bg-aw-green text-aw-blueblack px-3 py-1.5 rounded-lg hover:bg-aw-green/80 transition-colors duration-200"
                    >
                      <FontAwesomeIcon icon={["fas", "download"]} />
                      Descargar
                    </button>
                  ) : (
                    <span className="shrink-0 text-xs text-gray-500 px-3 py-1.5">
                      No disponible
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-700 bg-aw-bluedark-50 px-4 py-8 text-center">
              <FontAwesomeIcon
                icon={["fas", "folder-open"]}
                className="text-gray-600 text-3xl mb-2"
              />
              <p className="text-sm text-gray-400">
                Aún no se han generado informes fiscales para este evento.
              </p>
            </div>
          )}
        </div>

        {/* Botón generar nuevo informe */}
        <div className="flex justify-end">
          <button
            onClick={handleGenerarInforme}
            disabled={generandoInforme}
            className="flex items-center gap-2 bg-aw-green text-aw-blueblack font-semibold px-5 py-2.5 rounded-lg hover:bg-aw-green/80 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {generandoInforme ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={["fas", "file-excel"]} />
                Generar Nuevo Informe
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
